import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Verse {
  text: string;
  reference: string;
  why: string;
}

const verses: Verse[] = [
  {
    text: "For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.",
    reference: "Jeremiah 29:11",
    why: "This verse reminds us that God has a plan for our lives, filled with hope and purpose, even in challenging times."
  },
  {
    text: "May the God of hope fill you with all joy and peace as you trust in him, so that you may overflow with hope by the power of the Holy Spirit.",
    reference: "Romans 15:13",
    why: "This verse shows that trusting God allows His Spirit to fill us with joy, peace, and abundant hope."
  },
  {
    text: "The steadfast love of the Lord never ceases; his mercies never come to an end; they are new every morning; great is your faithfulness.",
    reference: "Lamentations 3:22-23",
    why: "This verse reminds us of God's unfailing love and new mercies every day, bringing hope even in trials."
  }
];

export default function JourneyHope() {
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  const navigateVerse = (direction: number) => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentVerseIndex((prev) => (prev + direction + verses.length) % verses.length);
      setIsVisible(true);
    }, 500);
  };

  const currentVerse = verses[currentVerseIndex];

  return (
    <div className="min-h-screen bg-[#f9f9f9] py-8 px-4 overflow-y-auto">
      <div className="max-w-3xl mx-auto pb-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="/lovable-uploads/97cec83c-b4fa-48c3-a12a-a678346efa1f.png" 
            alt="Grace Awakens Logo" 
            className="h-16 w-auto"
          />
        </div>

        {/* Scripture App Container */}
        <div className="bg-white rounded-lg shadow-lg p-8 relative mb-8">
          <h1 className="text-2xl font-semibold text-gray-800 mb-6">
            Journey to Hope
          </h1>

          {/* Navigation Arrows */}
          <button
            onClick={() => navigateVerse(-1)}
            className="absolute left-[-20px] top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Previous verse"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Scripture Content */}
          <div className={`transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <p className="text-lg text-gray-700 mb-4">
              "{currentVerse.text}"
            </p>
            <p className="text-grace-DEFAULT font-semibold mb-3">
              {currentVerse.reference}
            </p>
            <p className="text-gray-600 italic mb-6">
              {currentVerse.why}
            </p>
          </div>

          <button
            onClick={() => navigateVerse(1)}
            className="absolute right-[-20px] top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/80 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Next verse"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Back Button with Arrow */}
        <div className="text-center">
          <Button
            onClick={() => navigate(-1)}
            variant="secondary"
            className="hover:bg-gray-100 gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Journeys
          </Button>
        </div>
      </div>
    </div>
  );
}